import { loadUnitPreset } from "@/libs/units/presets";
import {
  Bubble_Point_Options_Enum,
  Dead_Oil_Options_Enum,
  Flow_Model_Enum,
  Gas_Compressibility_Options_Enum,
  Gas_Viscosity_Options_Enum,
  Oil_Density_Options_Enum,
  Oil_Volume_Factor_Options_Enum,
  Saturated_Oil_Options_Enum,
  Scenario_Reasons_Enum,
  Solution_Gas_Oil_Ratio_Options_Enum,
  Tubing_Flow_Options_Enum,
  Under_Saturated_Oil_Options_Enum,
  Water_Density_Options_Enum,
  Water_Solution_Gas_Options_Enum,
  Water_Viscosity_Options_Enum,
  Water_Volume_Factor_Options_Enum,
  Well_Gas_Type_Options_Enum,
  Well_Test_Gas_Input_Options_Enum,
  Well_Test_Liquid_Input_Options_Enum,
  Well_Test_Pressure_Input_Options_Enum,
  Natural_Gas_Separation_Options_Enum,
  Override_Options_Enum,
  useSealMotorOilQuery,
  Sizing_Pumping_Type_Enum,
  Display_Units_Enum,
  Casing_Flow_Options_Enum,
  Hps_Liquid_Type_Enum
} from "gql/api";
import { v4 } from "uuid";
import { type Ref } from "vue";
import type { Scenario, Sizing, WellProperties } from "./forms/formTypes";
import { DEFAULT_INPUT_LINE_VOLTAGE_V } from "@/libs/calculations/constants";
import { scenarioDefaultSealMotorOilVars } from "@/models/equipment/seal_motor_oil/predicates";

export default (
  sizing: Ref<Sizing>
): (() => Scenario & { well_properties: WellProperties }) => {
  const defaultSealMotorOil = useSealMotorOilQuery({
    variables: scenarioDefaultSealMotorOilVars
  });

  const isHps = sizing.value.pumping_type === Sizing_Pumping_Type_Enum.Hps;

  return () => {
    const parametersJournalId = v4();
    const correlationsJournalId = v4();
    const wellPropertiesJournalId = v4();
    const equipmentJournalId = v4();

    return {
      __typename: "scenarios",
      winning_revision_id: null,
      id: v4(),
      title: "New Scenario",
      notes: null,
      internal_notes: null,
      sizing_id: sizing.value.id,
      display_unit_overrides: isHps
        ? loadUnitPreset(Display_Units_Enum.UsHps) || {}
        : loadUnitPreset(sizing.value?.default_display_units) || {},
      reason: Scenario_Reasons_Enum.Quote,
      parameters_id: parametersJournalId,
      correlations_id: correlationsJournalId,
      well_properties_id: wellPropertiesJournalId,
      scenario_equipment_id: equipmentJournalId,
      correlations: {
        __typename: "scenario_correlations",
        winning_revision_id: null,
        id: correlationsJournalId,
        tubing_palmer_correction: false,
        casing_palmer_correction: false,
        salinity_ppm: 0,
        tubing_roughness_inches: 0.0015,
        casing_roughness_inches: 0.0015,
        tubing_flow: Tubing_Flow_Options_Enum.BeggsBrill,
        casing_flow: Casing_Flow_Options_Enum.BeggsBrill,
        bubble_point: Bubble_Point_Options_Enum.Standing,
        solution_gor: Solution_Gas_Oil_Ratio_Options_Enum.Standing,
        oil_volume_factor_saturated: Oil_Volume_Factor_Options_Enum.Standing,
        oil_volume_factor_undersaturated:
          Oil_Volume_Factor_Options_Enum.Standing,
        water_volume_factor: Water_Volume_Factor_Options_Enum.Gould,
        gas_compressibility: Gas_Compressibility_Options_Enum.DranchukPurvis,
        oil_density: Oil_Density_Options_Enum.Standing,
        dead_oil: Dead_Oil_Options_Enum.BeggsRobinson,
        saturated_oil: Saturated_Oil_Options_Enum.BeggsRobinson,
        under_saturated_oil: Under_Saturated_Oil_Options_Enum.BeggsRobinson,
        water_viscosity: Water_Viscosity_Options_Enum.VanWingen,
        water_density: Water_Density_Options_Enum.Gould,
        water_solution_gas: Water_Solution_Gas_Options_Enum.Mccain,
        gas_viscosity: Gas_Viscosity_Options_Enum.Lee,
        tubing_griffith_wallis: true,
        casing_griffith_wallis: true,
        natural_gas_separation: Natural_Gas_Separation_Options_Enum.Alhassan,
        tdh_tuning_factor_ratio: 1
      },
      well_properties: {
        __typename: "scenario_well_properties",
        winning_revision_id: null,
        id: wellPropertiesJournalId,
        oil_gravity_api: 38,
        water_specific_gravity: 1.05,
        gas_specific_gravity: 0.65,
        well_test_test_date: null,
        well_test_liquid_input_type:
          Well_Test_Liquid_Input_Options_Enum.TotalRateWaterCut,
        well_test_pressure_input_type:
          Well_Test_Pressure_Input_Options_Enum.Pressure,
        well_test_gas_input_type: Well_Test_Gas_Input_Options_Enum.GasOilRatio,
        bottomhole_temperature_fahrenheit: 170,
        surface_temperature_fahrenheit: 120,
        hps_pump_inlet_temperature_fahrenheit: 120,
        hps_liquid_type: isHps ? Hps_Liquid_Type_Enum.Multiphase : null,
        top_casing_id: null,
        top_casing_length_feet: null,
        bottom_casing_id: null,
        bottom_casing_length_feet: null,
        top_tubing_id: null,
        top_tubing_length_feet: null,
        bottom_tubing_id: null,
        bottom_tubing_length_feet: null,
        bubble_point_pressure_psia: null,
        fluid_viscosity_override_cp: null,
        deviation_survey_id: null,
        co2_percent: 0,
        h2s_percent: 0,
        n2_percent: 0,
        c1_methane_percent: 0,
        c2_ethane_percent: 0,
        c3_propane_percent: 0,
        ic4_isobutene_percent: 0,
        nc4_butane_percent: 0,
        ic5_isopentane_percent: 0,
        nc5_bentane_percent: 0,
        c6_hexane_percent: 0,
        c7_heptane_percent: 0,
        o2_percent: 0,
        ar_percent: 0,
        gas_type: Well_Gas_Type_Options_Enum.Dry,
        condensate_api_override: null,
        dew_point_pressure_psia_override: null,
        npsha_ft: null,
        top_of_perforations_measured_depth_feet: null,
        top_of_perforations_true_vertical_depth_feet: null,
        well_test_datum_point_measured_depth_feet: null,
        well_test_gas_rate_mscf_day: null,
        well_test_oil_rate_stb_day: null,
        well_test_productivity_index: null,
        well_test_producing_bottom_hole_pressure_psia: null,
        well_test_static_bottom_hole_pressure_psia: null,
        well_test_water_rate_stb_day: null,
        custom_fluid_properties: null
      },
      equipment: {
        __typename: "scenario_equipment",
        winning_revision_id: null,
        id: equipmentJournalId,
        mle_id: null,
        down_trans_id: null,
        seal_motor_oil_id:
          defaultSealMotorOil.data?.value?.seal_motor_oil[0]?.id,
        motor_id: null,
        include_y_tool: false,
        uphole_y_tool_id: null,
        downhole_y_tool_id: null,
        uphole_y_tool_bypass_tubing_order: [],
        uphole_y_tool_equipment_tubing_order: [],
        downhole_y_tool_bypass_tubing_order: [],
        downhole_y_tool_equipment_tubing_order: [],
        lower_motor_id: null,
        lower_sensor_id: null,
        lower_mle_id: null,
        lower_seal_motor_oil_id: null,
        lower_cables_order: [],
        lower_intakes_order: [],
        lower_pumps_order: [],
        lower_seals_order: [],
        parent_id: null,
        sensor_id: null,
        switchboard_id: null,
        up_trans_id: null,
        vsd_id: null,
        use_vsd: true,
        use_uphole_y_tool: false,
        use_downhole_y_tool: false,
        cables_order: [],
        r_xref_cables: [],
        intakes_order: [],
        r_xref_intakes: [],
        pumps_order: [],
        r_xref_pumps: [],
        seals_order: [],
        r_xref_seals: [],
        r_xref_uphole_y_tool_bypass_tubings: [],
        r_xref_uphole_y_tool_equipment_tubings: [],
        r_xref_downhole_y_tool_bypass_tubings: [],
        r_xref_downhole_y_tool_equipment_tubings: []
      },
      parameters: {
        __typename: "scenario_parameters",
        winning_revision_id: null,
        id: parametersJournalId,
        pump_hz: 60,
        has_compression_pump: !!isHps,
        motor_rerate_ratio: 1,
        vsd_input_voltage: DEFAULT_INPUT_LINE_VOLTAGE_V,
        // TODO: min freq will be set to 30 when 50hz scenarios are available
        minimum_frequency: 40,
        // TODO: max freq will be set to 50 when 50hz scenarios are available
        maximum_frequency: 60,
        intake_packer_installed: isHps,
        use_legacy_gas_head_rerate: false,
        include_casing_friction_loss: !isHps,
        bottom_casing_pressure_psia: null,
        bottom_tubing_pressure_psia: null,
        cable_cost_kw_hr_usd: null,
        intake_gas_separation_override_type: isHps
          ? Override_Options_Enum.Disable
          : Override_Options_Enum.Calculate,
        intake_override_gas_separation_percent: null,
        natural_gas_separation_override_type: isHps
          ? Override_Options_Enum.Disable
          : Override_Options_Enum.Calculate,
        natural_override_gas_separation_percent: null,
        motor_shroud_interior_diameter_inches: null,
        pump_override_gas_correction_percent: isHps ? 100 : null,
        pump_wear_head_correction_ratio: 1,
        pump_wear_brake_horsepower_correction_ratio: 1,
        surface_drive_incoming_power_volts: null,
        target_liquid_rate_std_barrel_day: null,
        flow_model: Flow_Model_Enum.StraightLine,
        lower_intake_packer_installed: null,
        lower_intake_override_gas_separation_percent: null,
        lower_intake_gas_separation_override_type:
          Override_Options_Enum.Calculate,
        lower_natural_override_gas_separation_percent: null,
        lower_natural_gas_separation_override_type:
          Override_Options_Enum.Calculate,
        lower_pump_hz: null,
        lower_has_compression_pump: null,
        lower_pump_override_gas_correction_percent: null,
        lower_motor_rerate_ratio: null,
        lower_motor_shroud_interior_diameter_inches: null,
        lower_use_legacy_gas_head_rerate: false,
        lower_include_casing_friction_loss: !isHps,
        lower_pump_wear_head_correction_ratio: 1,
        lower_pump_wear_brake_horsepower_correction_ratio: 1
      },
      r_performance_evaluations: [],
      pumping_type: sizing.value.pumping_type
    };
  };
};
